import { createSlice, } from '@reduxjs/toolkit';
/**
 * Check if the async action type is rejected
 */
export function isRejectedAction(action) {
    return action.type.endsWith('/rejected');
}
/**
 * Check if the async action type is pending
 */
export function isPendingAction(action) {
    return action.type.endsWith('/pending');
}
/**
 * Check if the async action type is completed
 */
export function isFulfilledAction(action) {
    return action.type.endsWith('/fulfilled');
}
const commonErrorProperties = ['name', 'message', 'stack', 'code'];
const API_FIELD_ERROR_MESSAGES = {
    branch: {
        inuse: 'You cannot delete this branch because it is used in cases.',
    },
    department: {
        inuse: 'Department is currently assigned to cases and cannot be deleted.',
    },
};
const API_FIELD_ERROR_CODE_MESSAGES = {
    inuse: 'Cannot delete because it is already in use.',
};
function formatApiFieldError(field, code) {
    const normalizedField = field.trim().toLowerCase();
    const normalizedCode = code.trim().toLowerCase();
    const fieldMessage = API_FIELD_ERROR_MESSAGES[normalizedField]?.[normalizedCode];
    if (fieldMessage)
        return fieldMessage;
    return API_FIELD_ERROR_CODE_MESSAGES[normalizedCode] ?? code.trim();
}
function readAxiosErrorData(error) {
    const axiosErr = error;
    if (axiosErr.isAxiosError && axiosErr.response?.data && typeof axiosErr.response.data === 'object') {
        return axiosErr.response.data;
    }
    const serialized = error;
    if (serialized.response?.data && typeof serialized.response.data === 'object') {
        return serialized.response.data;
    }
    return null;
}
/** Field-level messages from a JHipster / API error payload (array or object `fieldErrors`). */
export const extractApiFieldErrors = (error) => {
    const data = readAxiosErrorData(error);
    if (!data)
        return {};
    const fieldErrors = data.fieldErrors;
    if (Array.isArray(fieldErrors)) {
        const result = {};
        for (const entry of fieldErrors) {
            const fe = entry;
            const field = fe.field?.trim();
            const message = fe.message?.trim();
            if (field && message)
                result[field] = message;
        }
        return result;
    }
    if (fieldErrors && typeof fieldErrors === 'object') {
        const result = {};
        for (const [field, message] of Object.entries(fieldErrors)) {
            if (typeof message === 'string' && message.trim()) {
                result[field] = message.trim();
            }
        }
        return result;
    }
    return {};
};
/** Human-readable message from a rejected async thunk (axios or generic). */
export const resolveAsyncThunkErrorMessage = (error) => {
    if (!error)
        return 'Request failed';
    const data = readAxiosErrorData(error);
    if (data) {
        const fieldErrors = extractApiFieldErrors(error);
        const fieldErrorMessages = Object.entries(fieldErrors).map(([field, message]) => formatApiFieldError(field, message));
        if (fieldErrorMessages.length > 0)
            return fieldErrorMessages.join(' · ');
        if (typeof data.detail === 'string' && data.detail.trim())
            return data.detail.trim();
        if (typeof data.title === 'string' && data.title.trim() && data.title !== 'Validation Failed') {
            return data.title.trim();
        }
        if (typeof data.message === 'string' && data.message.trim() && !data.message.startsWith('error.')) {
            return data.message.trim();
        }
        if (typeof data.title === 'string' && data.title.trim())
            return data.title.trim();
    }
    if (error instanceof Error) {
        const msg = error.message?.trim();
        if (msg)
            return msg;
    }
    const serializedMessage = error.message;
    if (typeof serializedMessage === 'string' && serializedMessage.trim()) {
        return serializedMessage.trim();
    }
    return 'Request failed';
};
export const serializeAxiosError = (value) => {
    if (typeof value === 'object' && value !== null) {
        if (value.isAxiosError) {
            const simpleError = {};
            for (const property of commonErrorProperties) {
                if (typeof value[property] === 'string') {
                    simpleError[property] = value[property];
                }
            }
            if (value.response) {
                simpleError.response = {
                    data: value.response.data,
                    status: value.response.status,
                };
            }
            return simpleError;
        }
        const simpleError = {};
        for (const property of commonErrorProperties) {
            if (typeof value[property] === 'string') {
                simpleError[property] = value[property];
            }
        }
        return simpleError;
    }
    return { message: String(value) };
};
/**
 * A wrapper on top of createSlice from Redux Toolkit to extract
 * common reducers and matchers used by entities
 */
export const createEntitySlice = ({ name = '', initialState, reducers, extraReducers, skipRejectionHandling, }) => {
    return createSlice({
        name,
        initialState,
        reducers: {
            /**
             * Reset the entity state to initial state
             */
            reset() {
                return initialState;
            },
            ...reducers,
        },
        extraReducers(builder) {
            extraReducers(builder);
            /*
             * Common rejection logic is handled here.
             * If you want to add your own rejcetion logic, pass `skipRejectionHandling: true`
             * while calling `createEntitySlice`
             * */
            if (!skipRejectionHandling) {
                builder.addMatcher(isRejectedAction, (state, action) => {
                    state.loading = false;
                    state.updating = false;
                    state.updateSuccess = false;
                    const rejectedAction = action;
                    state.errorMessage = resolveAsyncThunkErrorMessage(rejectedAction.error);
                });
            }
        },
    });
};
